/**
 * Central tracking utilities: page_view sequencing and reload-safe deduplication.
 */

export type TrackingPersistence = 'session' | 'local';

const STORAGE_PREFIX = 'glowcard_tracked_';

const getStorage = (persistence: TrackingPersistence): Storage | null => {
    if (typeof window === 'undefined') {
        return null;
    }

    try {
        return persistence === 'local' ? window.localStorage : window.sessionStorage;
    } catch {
        return null;
    }
};

export const hasTrackedEvent = (dedupeKey: string, persistence: TrackingPersistence): boolean => {
    const storage = getStorage(persistence);

    if (!storage) {
        return false;
    }

    return storage.getItem(`${STORAGE_PREFIX}${dedupeKey}`) === '1';
};

export const markTrackedEvent = (dedupeKey: string, persistence: TrackingPersistence): void => {
    const storage = getStorage(persistence);

    if (!storage) {
        return;
    }

    storage.setItem(`${STORAGE_PREFIX}${dedupeKey}`, '1');
};

export const runTrackedEventOnce = (
    dedupeKey: string,
    persistence: TrackingPersistence,
    callback: () => void,
): void => {
    if (hasTrackedEvent(dedupeKey, persistence)) {
        return;
    }

    callback();
    markTrackedEvent(dedupeKey, persistence);
};

export const buildViewItemDedupeKey = (type: string, amount: number, background?: string): string => {
    return `view_item:${type}:${amount}:${background ?? 'default'}`;
};

export const buildViewCartDedupeKey = (
    items: Array<{ type: string; amount: number; quantity?: number; background?: string }>,
    total: number,
): string => {
    const signature = items
        .map((item) => `${item.type}:${item.amount}:${item.quantity ?? 1}:${item.background ?? ''}`)
        .join('|');

    return `view_cart:${signature}:${total}`;
};

export const buildBeginCheckoutDedupeKey = (paymentIntentId: string): string => {
    return `begin_checkout:${paymentIntentId}`;
};

export const buildPurchaseDedupeKey = (transactionId: string): string => {
    return `purchase:${transactionId}`;
};

export const pushPageView = (pageUrl?: string, pageTitle?: string): void => {
    if (typeof window === 'undefined') {
        return;
    }

    const location = pageUrl ?? window.location.href;
    const url = new URL(location, window.location.origin);

    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push({
        event: 'page_view',
        page_location: location,
        page_path: url.pathname + url.search,
        page_title: pageTitle ?? document.title,
        page_referrer: document.referrer || undefined,
    });
};

/**
 * Push an event to the dataLayer after the current synchronous execution context.
 *
 * Uses queueMicrotask so the push always runs after any synchronous code in the
 * current call stack (including Inertia's navigate handler). This guarantees that
 * page_view – pushed synchronously inside the navigate handler – always lands in
 * the dataLayer before any ecommerce event pushed from onMounted, regardless of
 * whether navigate fires before or after onMounted.
 */
export const pushToDataLayerAfterPageView = (eventData: Record<string, unknown>): void => {
    if (typeof window === 'undefined') {
        return;
    }

    queueMicrotask(() => {
        (window as any).dataLayer = (window as any).dataLayer || [];
        (window as any).dataLayer.push(eventData);
    });
};

export const pushToDataLayer = (eventData: Record<string, unknown>): void => {
    pushToDataLayerAfterPageView(eventData);
};
